import { LayoutGrid } from 'lucide-react';
import { extractApiErrorMessage } from '../../lib/apiError';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';

import { DomainCreateAPI, DomainUpdate } from '../../types';
import { domainService } from '../../services/domainService';
import { DomainForm } from '../../components/Domains/DomainForm';
import { PageLayout } from '../../components/Layout/PageLayout';
import { useTourStore } from '../../stores/tourStore';
import { useIsOrganizationAdmin, useEffectiveUser } from '../../stores/authStore';

const NewDomainPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = useIsOrganizationAdmin();
  const effectiveUser = useEffectiveUser();

  if (!isAdmin) {
    return <Navigate to="/domains" replace />;
  }

  const handleSubmit = async (data: DomainCreateAPI | DomainUpdate) => {
    try {
      setLoading(true);
      setError(null);

      // Create the domain
      const newDomain = await domainService.createDomain(data as DomainCreateAPI);

      // If user is in the "Complete User Journey" tour, advance to transition step (6) before
      // navigating so the tour continues on the domain detail page
      const { activeTourId, setStep } = useTourStore.getState();
      if (activeTourId === 'userJourney') {
        setStep(6);
      }

      // Navigate to the new domain detail page
      navigate(`/domains/${newDomain.id}`, {
        state: { message: t('newDomain.createSuccess') }
      });
    } catch (err: any) {
      console.error('Failed to create domain', err);
      setError(
        extractApiErrorMessage(err, t('newDomain.createError'))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/domains');
  };

  return (
    <PageLayout
      icon={<LayoutGrid className="h-6 w-6" />}
      title={t('newDomain.title')}
      subtitle={t('newDomain.subtitle')}
    >
      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Form */}
      <div>
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <DomainForm
            organizationId={effectiveUser?.organization_id ?? null}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      </div>

      {/* Help Section */}
      <div>
        <div className="bg-muted rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">
            {t('newDomain.aboutTitle')}
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t('newDomain.aboutDescription')}
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>{t('newDomain.aboutPoint1')}</li>
              <li>{t('newDomain.aboutPoint2')}</li>
              <li>{t('newDomain.aboutPoint3')}</li>
              <li>{t('newDomain.aboutPoint4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default NewDomainPage;
